import React, { useEffect } from 'react'
import { XMarkIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useCartStore } from '../stores/cartStore'
import { useNavigate } from 'react-router-dom'

const CartSidebar: React.FC = () => {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity,
    shipping,
    isLoadingShipping,
    calculateShipping,
    getSubtotal,
    getShippingCost,
    getGrandTotal,
    getTotalItems
  } = useCartStore()
  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen && items.length > 0) {
      calculateShipping()
    }
  }, [isOpen, items.length])


  const handleCheckout = () => {
    closeCart()
    navigate('/checkout')
  }

  if (!isOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={closeCart}
      />
      
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-xl transform transition-transform">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h2 className="text-lg font-semibold">Warenkorb ({getTotalItems()})</h2>
            <button
              onClick={closeCart}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {items.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                <div className="text-4xl mb-4">🛒</div>
                <p>Ihr Warenkorb ist leer</p>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.id} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-sm">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-xs">{item.device.brand} {item.device.deviceModel}</p>
                        {item.isExpress && (
                          <p className="text-orange-600 text-xs font-medium">Express-Service</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-indigo-600">
                          {(item.price * item.quantity).toFixed(2)}€
                        </div>
                        <p className="text-xs text-gray-600">
                          {item.quantity}x {item.price.toFixed(2)}€
                        </p>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="text-gray-400 hover:text-indigo-500 text-xs"
                        >
                          Entfernen
                        </button>
                      </div>
                    </div>

                    {/* Quantity Controls */}
                    <div className="flex items-center justify-between mt-3">
                      <span className="text-sm text-gray-600">Anzahl:</span>
                      <div className="flex items-center gap-2">
                        <button
                          className={`w-6 h-6 flex items-center justify-center border rounded transition-colors ${
                            item.quantity <= 1
                              ? 'border-indigo-300 text-indigo-500 hover:bg-indigo-50'
                              : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}
                          onClick={() => item.quantity <= 1 ? removeItem(item.id) : updateQuantity(item.id, item.quantity - 1)}
                        >
                          {item.quantity <= 1 ? <TrashIcon className="w-3 h-3" /> : '-'}
                        </button>
                        <span className="w-8 text-center font-semibold">
                          {item.quantity}
                        </span>
                        <button
                          className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={item.quantity >= 10}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t p-4 space-y-4">
              {/* Pricing breakdown */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Zwischensumme:</span>
                  <span>{getSubtotal().toFixed(2)}€</span>
                </div>
                
                <div className="flex justify-between text-sm">
                  <span>Versand:</span>
                  <span>
                    {isLoadingShipping ? (
                      <span className="text-gray-400">Wird berechnet...</span>
                    ) : getShippingCost() === 0 ? (
                      <span className="text-green-600 font-medium">Kostenlos</span>
                    ) : (
                      `${getShippingCost().toFixed(2)}€`
                    )}
                  </span>
                </div>
                
                {shipping && !shipping.isFreeShipping && (
                  <div className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                    💡 Noch {(shipping.freeShippingThreshold - shipping.subtotal).toFixed(2)}€ bis zum kostenlosen Versand!
                  </div>
                )}
                
                <hr className="my-2" />
                
                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Gesamt:</span>
                  <span className="text-indigo-600">
                    {isLoadingShipping ? '...' : getGrandTotal().toFixed(2)}€
                  </span>
                </div>
              </div>
              
              <button
                onClick={handleCheckout}
                disabled={isLoadingShipping}
                className="w-full bg-indigo-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Zur Kasse
              </button>
              
              <p className="text-xs text-gray-500 text-center">
                Kostenloser Versand ab 50€ • 12 Monate Garantie
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default CartSidebar